import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Progress } from '../../components/ui/progress';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Loader2, Upload, X, Check, ChevronLeft, ChevronRight, Search, Plus, Image as ImageIcon, ClipboardPaste, ChevronDown, Save, FileText, Trash2, Download, UserCheck, Eye, Info, Camera } from 'lucide-react';
import { toast } from 'sonner';
import { COUNTRIES, getCountryName } from '../../utils/countries';
import ClipboardPasteModal from '../../components/ClipboardPasteModal';
import { FormField, FormSelect, MultiSelectCheckboxes, ReviewSection, WizardStepper, OrangeCheckbox, ReviewProductHeader } from '../shared/WizardComponents';
import { useDownload, formatBytes } from '../../context/DownloadContext';
import { SubmittalPreviewModal } from '../../components/SubmittalPreviewModal';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { useSubmittalsManager, commitSubmittals } from '../shared/useSubmittalsManager';
import { WizardSubmittalsStep, WizardSubmittalsReview } from '../shared/WizardSubmittalsStep';

// ═══════════════════════════════════════════════════════════════
// WIZARD CONFIGURATION — Step definitions & field mapping
// ═══════════════════════════════════════════════════════════════
const WIZARD_STEPS = [
  { id: 1, title: 'Product Identification' },
  { id: 2, title: 'Material & Physical Properties' },
  { id: 3, title: 'Water Resistance & Drainage' },
  { id: 4, title: 'Vapor Permeability' },
  { id: 5, title: 'Fire & Code Compliance' },
  { id: 6, title: 'Installation Parameters' },
  { id: 7, title: 'Interface & Continuity' },
  { id: 8, title: 'Durability & Exposure Limits' },
  { id: 9, title: 'Submittals' },
  { id: 10, title: 'Field Verification' },
  { id: 11, title: 'Review Summary' },
];
const TOTAL_STEPS = WIZARD_STEPS.length;

// Step-to-field mapping for validation navigator (deterministic first-invalid-step jump)
const STEP_FIELD_PREFIXES = {
  1: ['productNameModel', 'manufacturerId', 'countryCode', 'imageFile', 'wrbType', 'scopeCoverage'],
  2: ['materialProperties'],
  3: ['waterResistance'],
  4: ['vaporPermeability'],
  5: ['fireCodeCompliance'],
  6: ['installationParameters'],
  7: ['interfaceContinuity'],
  8: ['durabilityExposure'],
  9: ['submittals'],
  10: [],
  11: [],
};

const DOCUMENT_TYPES = ['Product Data Sheet', 'Test Report', 'Installation Guide', 'Warranty', 'Compatibility Letters'];

const INITIAL_FORM = {
  manufacturerId: '', productNameModel: '', countryCode: '',
  wrbType: '', scopeCoverage: '',
  imageFile: null, imagePreview: null,
  materialProperties: { materialType: '', thickness: '', reinforcement: '', surfaceType: '' },
  waterResistance: { waterResistanceTest: '', hydrostaticPressureRating: '', drainagePlaneCapability: '', integratedDrainageGap: '' },
  vaporPermeability: { vaporPermeance: '', astmE96Method: '', vaporClassification: '' },
  fireCodeCompliance: { nfpa285Compatibility: '', astmE84: '', iccEsReportNo: '', exteriorWallAssemblyRef: '' },
  installationParameters: { applicationMethod: '', substrateType: '', maxFastenerSpacing: '', lappingRequirements: '', tempWeatherLimitations: '' },
  interfaceContinuity: { interfacesWith: '', continuityResponsibility: '', otherSubcontractor: '', transitionMembraneRequired: '' },
  durabilityExposure: { uvExposureLimit: '', tearResistance: '', repairMethod: '', temporaryExposureAllowed: '' },
  fieldVerification: { installedThickness: '', nonConformance: '', inspectorComments: '', installationPhotos: [] },
  submittals: [],
};

const STORAGE_PREFIX = 'specard_wb_wizard_v1_';
const getStorageKey = (companyId, draftId) => `${STORAGE_PREFIX}${companyId}_${draftId || 'new'}`;
const saveWizardState = (companyId, draftId, state) => {
  try {
    const f = state.form;
    sessionStorage.setItem(getStorageKey(companyId, draftId), JSON.stringify({
      step: state.step, draftId: state.draftId,
      form: {
        manufacturerId: f.manufacturerId, productNameModel: f.productNameModel, countryCode: f.countryCode,
        wrbType: f.wrbType, scopeCoverage: f.scopeCoverage,
        imagePreview: f.imagePreview && !f.imagePreview.startsWith('blob:') ? f.imagePreview : null,
        materialProperties: f.materialProperties, waterResistance: f.waterResistance,
        vaporPermeability: f.vaporPermeability, fireCodeCompliance: f.fireCodeCompliance,
        installationParameters: f.installationParameters, interfaceContinuity: f.interfaceContinuity,
        durabilityExposure: f.durabilityExposure,
        fieldVerification: f.fieldVerification,
        submittals: f.submittals,
      },
    }));
  } catch { /* quota */ }
};
const loadWizardState = (companyId, draftId) => { try { const r = sessionStorage.getItem(getStorageKey(companyId, draftId)); return r ? JSON.parse(r) : null; } catch { return null; } };
const clearWizardState = (companyId, draftId) => { try { sessionStorage.removeItem(getStorageKey(companyId, draftId)); if (draftId) sessionStorage.removeItem(getStorageKey(companyId, 'new')); } catch {} };

const mergeFormDefaults = (saved) => {
  if (!saved) return { ...INITIAL_FORM };
  return {
    ...INITIAL_FORM, ...saved,
    materialProperties: { materialType: '', thickness: '', reinforcement: '', surfaceType: '', ...(saved.materialProperties || {}) },
    waterResistance: { waterResistanceTest: '', hydrostaticPressureRating: '', drainagePlaneCapability: '', integratedDrainageGap: '', ...(saved.waterResistance || {}) },
    vaporPermeability: { vaporPermeance: '', astmE96Method: '', vaporClassification: '', ...(saved.vaporPermeability || {}) },
    fireCodeCompliance: { nfpa285Compatibility: '', astmE84: '', iccEsReportNo: '', exteriorWallAssemblyRef: '', ...(saved.fireCodeCompliance || {}) },
    installationParameters: { applicationMethod: '', substrateType: '', maxFastenerSpacing: '', lappingRequirements: '', tempWeatherLimitations: '', ...(saved.installationParameters || {}) },
    interfaceContinuity: { interfacesWith: '', continuityResponsibility: '', otherSubcontractor: '', transitionMembraneRequired: '', ...(saved.interfaceContinuity || {}) },
    durabilityExposure: { uvExposureLimit: '', tearResistance: '', repairMethod: '', temporaryExposureAllowed: '', ...(saved.durabilityExposure || {}) },
    fieldVerification: { ...INITIAL_FORM.fieldVerification, ...(saved.fieldVerification || {}), installationPhotos: saved.fieldVerification?.installationPhotos || [] },
    submittals: saved.submittals || [],
  };
};

const isNumericValue = (v) => !v || /^-?\d*\.?\d*$/.test(v.trim());

// ===== Photo Viewer Modal =====
const WBPhotoViewerModal = ({ photos, currentIndex, onIndexChange, onClose }) => {
  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') onIndexChange(prev => prev > 0 ? prev - 1 : photos.length - 1);
      if (e.key === 'ArrowRight') onIndexChange(prev => prev < photos.length - 1 ? prev + 1 : 0);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [photos.length, onClose, onIndexChange]);
  const photo = photos[currentIndex];
  if (!photo) return null;
  return (
    <div className="absolute inset-0 z-[60] flex items-center justify-center rounded-lg overflow-hidden" data-testid="wb-fv-photo-viewer-modal">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      {photos.length > 1 && (
        <>
          <button className="absolute left-3 top-1/2 -translate-y-1/2 z-[70] bg-white hover:bg-slate-100 text-slate-700 rounded-full w-10 h-10 flex items-center justify-center shadow-lg border transition-colors"
            onClick={() => onIndexChange(currentIndex > 0 ? currentIndex - 1 : photos.length - 1)}
            data-testid="wb-fv-viewer-prev"><ChevronLeft className="h-5 w-5" /></button>
          <button className="absolute right-3 top-1/2 -translate-y-1/2 z-[70] bg-white hover:bg-slate-100 text-slate-700 rounded-full w-10 h-10 flex items-center justify-center shadow-lg border transition-colors"
            onClick={() => onIndexChange(currentIndex < photos.length - 1 ? currentIndex + 1 : 0)}
            data-testid="wb-fv-viewer-next"><ChevronRight className="h-5 w-5" /></button>
        </>
      )}
      <div className="relative z-[65] flex flex-col items-center bg-white rounded-xl shadow-2xl p-4 max-w-[85%] max-h-[90%]">
        <button className="absolute -top-3 -right-3 bg-slate-700 text-white rounded-full w-8 h-8 flex items-center justify-center shadow-lg hover:bg-slate-900 z-20"
          onClick={onClose} data-testid="wb-fv-viewer-close"><X className="h-4 w-4" /></button>
        <img src={photo.url} alt={photo.fileName} className="max-w-full max-h-[70%] rounded-lg object-contain" data-testid="wb-fv-viewer-image" />
        <div className="mt-3 text-center">
          <p className="text-sm text-slate-800 font-medium">{photo.fileName}</p>
          <p className="text-xs text-slate-400">{currentIndex + 1} / {photos.length}</p>
        </div>
      </div>
    </div>
  );
};

export const AddWeatherBarrierProductWizard = ({ open, onOpenChange, onComplete, onRefresh, editProduct, companyId, projectId, csiItemId }) => {
  const isEdit = !!editProduct;
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(INITIAL_FORM);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [finalizing, setFinalizing] = useState(false);
  const [draftId, setDraftId] = useState(null);
  const initialFormRef = useRef(INITIAL_FORM);
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  const [showPasteModal, setShowPasteModal] = useState(false);
  const [pasteUploading, setPasteUploading] = useState(false);
  const [photoUploading, setPhotoUploading] = useState(false);
  const [viewerPhotoIndex, setViewerPhotoIndex] = useState(null);
  const { startDownload, cancelDownload, getState: getDlState } = useDownload();
  const fileInputRef = useRef(null);
  const submittals = useSubmittalsManager();

  const [manufacturers, setManufacturers] = useState([]);
  const [mfrSearch, setMfrSearch] = useState('');
  const [mfrDropdownOpen, setMfrDropdownOpen] = useState(false);
  const [creatingMfr, setCreatingMfr] = useState(false);
  const mfrRef = useRef(null);

  const [countrySearch, setCountrySearch] = useState('');
  const [countryDropdownOpen, setCountryDropdownOpen] = useState(false);
  const countryRef = useRef(null);

  // Derived
  const selectedManufacturer = manufacturers.find(m => m.id === form.manufacturerId);
  const selectedCountry = COUNTRIES.find(c => c.code === form.countryCode);
  const isReviewStep = step === TOTAL_STEPS;

  // ===== Effects =====
  useEffect(() => {
    if (!open) return;
    const loadMfrs = async () => { try { const r = await axios.get(`${API}/thermal-insulation-manufacturers`); setManufacturers(r.data || []); } catch {} };
    loadMfrs();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (editProduct) {
      const ef = {
        manufacturerId: editProduct.manufacturerId || '', productNameModel: editProduct.productNameModel || '', countryCode: editProduct.countryCode || '',
        wrbType: editProduct.wrbType || '', scopeCoverage: editProduct.scopeCoverage || '',
        imageFile: null, imagePreview: editProduct.imageUrl || null,
        materialProperties: editProduct.materialProperties || { materialType: '', thickness: '', reinforcement: '', surfaceType: '' },
        waterResistance: editProduct.waterResistance || { waterResistanceTest: '', hydrostaticPressureRating: '', drainagePlaneCapability: '', integratedDrainageGap: '' },
        vaporPermeability: editProduct.vaporPermeability || { vaporPermeance: '', astmE96Method: '', vaporClassification: '' },
        fireCodeCompliance: editProduct.fireCodeCompliance || { nfpa285Compatibility: '', astmE84: '', iccEsReportNo: '', exteriorWallAssemblyRef: '' },
        installationParameters: editProduct.installationParameters || { applicationMethod: '', substrateType: '', maxFastenerSpacing: '', lappingRequirements: '', tempWeatherLimitations: '' },
        interfaceContinuity: editProduct.interfaceContinuity || { interfacesWith: '', continuityResponsibility: '', otherSubcontractor: '', transitionMembraneRequired: '' },
        durabilityExposure: editProduct.durabilityExposure || { uvExposureLimit: '', tearResistance: '', repairMethod: '', temporaryExposureAllowed: '' },
        fieldVerification: editProduct.fieldVerification,
        submittals: editProduct.submittals || [],
        qrToken: editProduct.qrToken,
      };
      setForm(mergeFormDefaults(ef));
      setDraftId(editProduct.id);
      submittals.init(editProduct.submittals || []);
      setStep(1);
      initialFormRef.current = mergeFormDefaults(ef);
    } else {
      clearWizardState(companyId, 'new');
      setForm({ ...INITIAL_FORM }); setStep(1); setDraftId(null); initialFormRef.current = { ...INITIAL_FORM };
      submittals.init([]);
    }
    setErrors({});
  }, [open, editProduct, companyId]);

  // Close outside dropdowns
  useEffect(() => {
    const h = (e) => { if (mfrRef.current && !mfrRef.current.contains(e.target)) setMfrDropdownOpen(false); if (countryRef.current && !countryRef.current.contains(e.target)) setCountryDropdownOpen(false); };
    document.addEventListener('mousedown', h); return () => document.removeEventListener('mousedown', h);
  }, []);

  // Auto-save to sessionStorage
  useEffect(() => {
    if (!open || isEdit) return;
    saveWizardState(companyId, draftId, { step, draftId, form });
  }, [step, form, draftId, open, isEdit, companyId]);

  // Helpers
  const updateField = (field, value) => { setForm(prev => ({ ...prev, [field]: value })); setErrors(prev => { const n = { ...prev }; delete n[field]; return n; }); };
  const updateNested = (group, field, value) => { setForm(prev => ({ ...prev, [group]: { ...prev[group], [field]: value } })); setErrors(prev => { const n = { ...prev }; delete n[`${group}.${field}`]; return n; }); };

  const handleImageSelect = (e) => { const f = e.target.files?.[0]; if (f) { updateField('imageFile', f); updateField('imagePreview', URL.createObjectURL(f)); } };
  const handleRemoveImage = () => { updateField('imageFile', null); updateField('imagePreview', null); if (fileInputRef.current) fileInputRef.current.value = ''; };

  const parseFractionValue = (v) => {
    if (!v) return null;
    const s = v.toString().trim();
    const mixed = s.match(/^(\d+)\s+(\d+)\/(\d+)$/);
    if (mixed) return parseInt(mixed[1]) + parseInt(mixed[2]) / parseInt(mixed[3]);
    const frac = s.match(/^(\d+)\/(\d+)$/);
    if (frac) return parseInt(frac[1]) / parseInt(frac[2]);
    const num = parseFloat(s);
    return isNaN(num) ? null : num;
  };

  const isDirty = (() => {
    if (submittals.isDirty) return true;
    const i = initialFormRef.current;
    if (!i) return false;
    if (form.imageFile) return true;
    if (JSON.stringify(form.fieldVerification) !== JSON.stringify(i.fieldVerification)) return true;
    const a = { ...form, imageFile: null, imagePreview: null, fieldVerification: null };
    const b = { ...i, imageFile: null, imagePreview: null, fieldVerification: null };
    return JSON.stringify(a) !== JSON.stringify(b);
  })();
  const resetAndClose = () => { if (draftId) clearWizardState(companyId, draftId); clearWizardState(companyId, 'new'); setForm({ ...INITIAL_FORM }); setStep(1); setErrors({}); setDraftId(null); setShowCloseConfirm(false); submittals.rollback(); onOpenChange(false); };
  const handleCloseAttempt = () => { if (isDirty) setShowCloseConfirm(true); else resetAndClose(); };

  // ===== Validation =====
  const validateDraft = () => {
    const e = {};
    if (!form.productNameModel.trim()) e.productNameModel = 'Product Name / Model is required';
    setErrors(e); return !Object.keys(e).length;
  };

  const validateAllSteps = () => {
    const e = {};
    // Step 1
    if (!form.productNameModel.trim()) e.productNameModel = 'Product Name / Model is required';
    if (!form.manufacturerId) e.manufacturerId = 'Manufacturer is required';
    if (!form.wrbType) e.wrbType = 'WRB Type is required';
    if (!form.scopeCoverage) e.scopeCoverage = 'Scope Coverage is required';
    // Step 2 — Material & Physical Properties
    if (form.materialProperties.thickness && !/^\d*\.?\d*$/.test(form.materialProperties.thickness.trim())) e['materialProperties.thickness'] = 'Thickness must be numeric';
    // Step 3 — Water Resistance & Drainage
    if (!form.waterResistance.waterResistanceTest) e['waterResistance.waterResistanceTest'] = 'Water Resistance Test is required';
    if (!form.waterResistance.drainagePlaneCapability) e['waterResistance.drainagePlaneCapability'] = 'Drainage Plane Capability is required';
    if (form.waterResistance.hydrostaticPressureRating && !/^\d+(\.\d+)?$/.test(form.waterResistance.hydrostaticPressureRating.trim())) e['waterResistance.hydrostaticPressureRating'] = 'Must be numeric';
    if (form.waterResistance.integratedDrainageGap && !/^(\d+(\.\d+)?|\d+\/\d+)$/.test(form.waterResistance.integratedDrainageGap.trim())) e['waterResistance.integratedDrainageGap'] = 'Must be numeric or fraction (e.g., 0.25 or 1/4)';
    if (form.vaporPermeability.vaporPermeance && !/^\d+(\.\d+)?$/.test(form.vaporPermeability.vaporPermeance.trim())) e['vaporPermeability.vaporPermeance'] = 'Must be numeric';
    if (form.durabilityExposure.tearResistance && !/^\d+(\.\d+)?$/.test(form.durabilityExposure.tearResistance.trim())) e['durabilityExposure.tearResistance'] = 'Must be numeric';
    // Step 4 — Vapor Permeability
    if (!form.vaporPermeability.astmE96Method) e['vaporPermeability.astmE96Method'] = 'ASTM E96 Method is required';
    if (!form.vaporPermeability.vaporClassification) e['vaporPermeability.vaporClassification'] = 'Vapor Classification is required';
    // Step 5 — Fire & Code Compliance
    if (!form.fireCodeCompliance.nfpa285Compatibility) e['fireCodeCompliance.nfpa285Compatibility'] = 'NFPA 285 Compatibility is required';
    if (!form.fireCodeCompliance.astmE84) e['fireCodeCompliance.astmE84'] = 'ASTM E84 is required';
    // Step 6 — Installation Parameters
    if (!form.installationParameters.applicationMethod) e['installationParameters.applicationMethod'] = 'Application Method is required';
    if (!form.installationParameters.substrateType) e['installationParameters.substrateType'] = 'Substrate Type is required';
    if (form.installationParameters.lappingRequirements && !/^\d*\.?\d*$/.test(form.installationParameters.lappingRequirements.trim())) e['installationParameters.lappingRequirements'] = 'Must be numeric';
    // Step 7 — Interface & Continuity
    if (!form.interfaceContinuity.interfacesWith) e['interfaceContinuity.interfacesWith'] = 'Interfaces with is required';
    if (!form.interfaceContinuity.transitionMembraneRequired) e['interfaceContinuity.transitionMembraneRequired'] = 'Transition Membrane Required is required';
    if (!form.interfaceContinuity.continuityResponsibility) e['interfaceContinuity.continuityResponsibility'] = 'Continuity Responsibility is required';
    if (form.interfaceContinuity.continuityResponsibility === 'Other' && !form.interfaceContinuity.otherSubcontractor?.trim()) e['interfaceContinuity.otherSubcontractor'] = 'Please specify the party/subcontractor';
    // Step 8 — Durability & Exposure Limits
    if (!form.durabilityExposure.temporaryExposureAllowed) e['durabilityExposure.temporaryExposureAllowed'] = 'Temporary Exposure Allowed is required';
    if (form.durabilityExposure.uvExposureLimit && !/^\d*\.?\d*$/.test(form.durabilityExposure.uvExposureLimit.trim())) e['durabilityExposure.uvExposureLimit'] = 'Must be numeric';
    // Step 9
    // Step 9 — Submittals (optional)
    // Step 10 — Field Verification
    if (form.fieldVerification.nonConformance === 'Yes' && !form.fieldVerification.inspectorComments?.trim()) {
      e['fieldVerification.inspectorComments'] = 'Inspector Comments are required when Non-conformance is detected';
    }
    setErrors(e);
    return e;
  };

  const getFirstErrorStep = (e) => {
    for (let stepIdx = 1; stepIdx <= 10; stepIdx++) {
      const prefixes = STEP_FIELD_PREFIXES[stepIdx] || [];
      const hasError = Object.keys(e).some(key => prefixes.some(p => key === p || key.startsWith(p + '.')));
      if (hasError) return stepIdx;
    }
    return 1;
  };

  // Build payload
  const buildStepPayload = () => ({
    wrbType: form.wrbType, scopeCoverage: form.scopeCoverage,
    materialProperties: form.materialProperties,
    waterResistance: form.waterResistance,
    vaporPermeability: form.vaporPermeability,
    fireCodeCompliance: form.fireCodeCompliance,
    installationParameters: form.installationParameters,
    interfaceContinuity: form.interfaceContinuity,
    durabilityExposure: form.durabilityExposure,
    ...(form.fieldVerification.installedThickness || form.fieldVerification.nonConformance || form.fieldVerification.inspectorComments || form.fieldVerification.installationPhotos?.length ? { fieldVerification: form.fieldVerification } : {}),
  });

  // ===== Save as Draft =====
  const handleSaveDraft = async () => {
    if (!validateDraft()) { toast.error('Please fill in required fields on Step 1 before saving'); setStep(1); return; }
    setSaving(true);
    try {
      let pid = draftId;
      const payload = { manufacturerId: form.manufacturerId, productNameModel: form.productNameModel.trim(), countryCode: form.countryCode, status: 'draft', csiItemId, ...buildStepPayload() };
      if (!pid) {
        const res = await axios.post(`${API}/weather-barrier-products/${projectId}`, payload);
        pid = res.data.id;
        setDraftId(pid);
        setForm(prev => ({ ...prev, qrToken: res.data.qrToken }));
      } else {
        await axios.patch(`${API}/weather-barrier-products/${projectId}/${pid}`, payload);
      }
      if (form.imageFile) {
        const fd = new FormData(); fd.append('file', form.imageFile);
        const imgRes = await axios.post(`${API}/weather-barrier-products/${projectId}/${pid}/upload-image`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        setForm(prev => ({ ...prev, imagePreview: imgRes.data.url || imgRes.data.imageUrl, imageFile: null }));
      }
      if (submittals.isDirty) {
        await commitSubmittals(submittals, pid, projectId, 'weather-barrier-products', axios, API);
        const fresh = await axios.get(`${API}/weather-barrier-products/${projectId}/${pid}`);
        submittals.init(fresh.data?.submittals || []);
      }
      initialFormRef.current = { ...form, imageFile: null };
      saveWizardState(companyId, pid, { step, draftId: pid, form });
      toast.success('Draft saved');
      if (onRefresh) onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save draft'); }
    finally { setSaving(false); }
  };

  // ===== Final Save (only from Review) =====
  const handleFinalSave = async () => {
    const validationErrors = validateAllSteps();
    if (Object.keys(validationErrors).length > 0) {
      const targetStep = getFirstErrorStep(validationErrors);
      if (targetStep !== step) setStep(targetStep);
      toast.error('Please complete all required fields before finalizing');
      return;
    }
    setFinalizing(true);
    try {
      let pid = draftId;
      const payload = { manufacturerId: form.manufacturerId, productNameModel: form.productNameModel.trim(), countryCode: form.countryCode, status: 'active', csiItemId, ...buildStepPayload() };
      if (!pid) { const res = await axios.post(`${API}/weather-barrier-products/${projectId}`, payload); pid = res.data.id; }
      else { await axios.patch(`${API}/weather-barrier-products/${projectId}/${pid}`, payload); }
      if (form.imageFile) { const fd = new FormData(); fd.append('file', form.imageFile); await axios.post(`${API}/weather-barrier-products/${projectId}/${pid}/upload-image`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }); }
      if (submittals.isDirty) {
        await commitSubmittals(submittals, pid, projectId, 'weather-barrier-products', axios, API);
      }
      toast.success(isEdit ? 'Product updated' : 'Product created');
      clearWizardState(companyId, pid); clearWizardState(companyId, 'new');
      if (onComplete) onComplete(); resetAndClose();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save product'); }
    finally { setFinalizing(false); }
  };

  // Navigation
  const handleNext = () => setStep(prev => Math.min(prev + 1, TOTAL_STEPS));
  const handleBack = () => setStep(prev => Math.max(prev - 1, 1));

  // Submittal preview
  const [previewSubmittal, setPreviewSubmittal] = useState(null);
  const handlePreviewSubmittal = async (submittal) => {
    if (!draftId || submittal._status === 'pending') return;
    try {
      const res = await axios.get(`${API}/weather-barrier-products/${projectId}/${draftId}/submittals/${submittal.id}/preview-url`);
      setPreviewSubmittal({ ...submittal, cloudinaryUrl: res.data?.url });
    } catch { toast.error('Failed to load preview'); }
  };

  // Clipboard paste
  const handlePasteImage = async (blob) => {
    setPasteUploading(true);
    try {
      const file = new File([blob], `paste_${Date.now()}.png`, { type: blob.type });
      updateField('imageFile', file);
      updateField('imagePreview', URL.createObjectURL(file));
      setShowPasteModal(false);
      toast.success('Image pasted');
    } finally { setPasteUploading(false); }
  };

  // Manufacturer create
  const handleCreateMfr = async (name) => {
    if (!name.trim()) return;
    setCreatingMfr(true);
    try {
      const res = await axios.post(`${API}/thermal-insulation-manufacturers`, { name: name.trim() });
      setManufacturers(prev => [...prev, res.data]);
      updateField('manufacturerId', res.data.id);
      setMfrDropdownOpen(false); setMfrSearch('');
      toast.success('Manufacturer created');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to create manufacturer'); }
    finally { setCreatingMfr(false); }
  };

  // Filtered lists
  const filteredMfrs = manufacturers.filter(m => m.name?.toLowerCase().includes(mfrSearch.toLowerCase())).sort((a, b) => a.name.localeCompare(b.name));
  const filteredCountries = COUNTRIES.filter(c => c.name.toLowerCase().includes(countrySearch.toLowerCase()));

  return (
    <>
      <Dialog open={open} onOpenChange={handleCloseAttempt}>
        <DialogContent className="max-w-4xl p-0 flex flex-col" style={{ height: '75vh', maxHeight: '85vh', minHeight: '400px' }} data-testid="wb-wizard-dialog">
          {/* Fixed Header */}
          <div className="px-6 pt-5 pb-3 border-b shrink-0">
            <DialogHeader>
              <DialogTitle>{isEdit ? 'Edit Weather Barrier Product' : 'Add Weather Barrier Product'}</DialogTitle>
              <DialogDescription>Step {step} of {TOTAL_STEPS}: {WIZARD_STEPS[step - 1]?.title}</DialogDescription>
            </DialogHeader>
            <WizardStepper steps={WIZARD_STEPS} currentStep={step} onStepClick={setStep} testIdPrefix="wb" />
          </div>

          {/* Scrollable Body */}
          <div className="flex-1 overflow-y-auto px-6 py-4" data-testid="wb-wizard-body">
            {/* ════════════ STEP 1: Product Information ════════════ */}
            {step === 1 && (
              <div className="space-y-4" data-testid="wb-wizard-step-1">
                <h4 className="font-semibold text-sm border-b pb-2 mb-3">Product Identification</h4>
                <input ref={fileInputRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={handleImageSelect} data-testid="wb-image-file-input" />
                <div className="grid grid-cols-1 md:grid-cols-[240px_1fr] gap-4 items-stretch" data-testid="wb-product-info-grid">
                  {/* Image — left column */}
                  <div className={`border-2 border-dashed rounded-lg flex flex-col items-center justify-center relative overflow-hidden bg-slate-50 transition-colors min-h-[220px] ${errors.imageFile ? 'border-red-400 bg-red-50/30' : 'border-slate-300'}`} data-testid="wb-image-upload-area">
                    {form.imagePreview ? (
                      <><img src={form.imagePreview} alt="Preview" className="w-full h-full object-contain p-2" data-testid="wb-image-preview" onError={(e) => { e.target.style.display = 'none'; }} /><button type="button" className="absolute top-2 right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center hover:bg-red-600 z-10" onClick={handleRemoveImage} data-testid="wb-remove-image-btn"><X className="h-3 w-3" /></button></>
                    ) : (
                      <div className="flex flex-col items-center justify-center gap-2 p-4 text-center"><ImageIcon className="h-10 w-10 text-slate-300" /><p className="text-xs text-slate-400">Product Image</p><p className="text-[10px] text-slate-400">PNG, JPG, WebP (max 5MB)</p></div>
                    )}
                    <div className="absolute bottom-0 inset-x-0 bg-white/90 border-t border-slate-200 flex items-center justify-center gap-1.5 py-1.5 px-2">
                      <Button type="button" variant="ghost" size="sm" className="h-7 text-xs px-2" onClick={() => setShowPasteModal(true)} data-testid="wb-paste-image-btn"><ClipboardPaste className="h-3.5 w-3.5 mr-1" />Paste</Button>
                      <Button type="button" variant="ghost" size="sm" className="h-7 text-xs px-2" onClick={() => fileInputRef.current?.click()} data-testid="wb-upload-image-btn"><Upload className="h-3.5 w-3.5 mr-1" />{form.imagePreview ? 'Change' : 'Upload'}</Button>
                    </div>
                    {errors.imageFile && <p className="absolute top-1 left-2 text-[10px] text-red-500 bg-white/80 px-1 rounded">{errors.imageFile}</p>}
                  </div>
                  {/* Fields — right column */}
                  <div className="space-y-3">
                    {/* Manufacturer dropdown */}
                    <div className="space-y-1">
                      <Label className="text-sm">Manufacturer *</Label>
                      <div className="relative" ref={mfrRef}>
                        <button type="button" onClick={() => setMfrDropdownOpen(!mfrDropdownOpen)}
                          className={`w-full flex items-center justify-between px-3 py-2 border rounded-md text-sm ${errors.manufacturerId ? 'border-red-400' : 'border-slate-300'} bg-white hover:bg-slate-50`}
                          data-testid="wb-manufacturer-dropdown"
                        >
                          <span className={selectedManufacturer ? 'text-slate-900' : 'text-slate-400'}>{selectedManufacturer?.name || 'Select manufacturer...'}</span>
                          <ChevronDown className="h-4 w-4 text-slate-400" />
                        </button>
                        {mfrDropdownOpen && (
                          <div className="absolute z-50 mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-lg max-h-52 overflow-auto">
                            <div className="sticky top-0 bg-white p-2 border-b"><div className="relative"><Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" /><Input className="pl-8" placeholder="Search manufacturer..." value={mfrSearch} onChange={(e) => setMfrSearch(e.target.value)} autoFocus data-testid="wb-mfr-search-input" /></div></div>
                            {filteredMfrs.map(m => (
                              <button key={m.id} type="button" onClick={() => { updateField('manufacturerId', m.id); setMfrDropdownOpen(false); setMfrSearch(''); }}
                                className="w-full text-left px-3 py-2 text-sm hover:bg-orange-50 flex items-center justify-between" data-testid={`wb-mfr-option-${m.id}`}
                              >
                                <span>{m.name}</span>
                                <div className="flex items-center gap-1">
                                  {m.scope !== 'global' && <UserCheck className="h-3 w-3 text-orange-400" title="My manufacturer" />}
                                  {m.id === form.manufacturerId && <Check className="h-4 w-4 text-orange-500" />}
                                </div>
                              </button>
                            ))}
                            {mfrSearch.trim() && !filteredMfrs.some(m => m.name.toLowerCase() === mfrSearch.toLowerCase()) && (
                              <button type="button" onClick={() => handleCreateMfr(mfrSearch)} disabled={creatingMfr}
                                className="w-full text-left px-3 py-2 text-sm text-orange-600 hover:bg-orange-50 flex items-center gap-1 border-t" data-testid="wb-create-mfr-btn"
                              >{creatingMfr ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3" />} Create "{mfrSearch.trim()}"</button>
                            )}
                          </div>
                        )}
                      </div>
                      {errors.manufacturerId && <p className="text-xs text-red-500">{errors.manufacturerId}</p>}
                    </div>
                    <FormField label="Product Name / Model" required error={errors.productNameModel}>
                      <Input placeholder="Enter product or system name" value={form.productNameModel} onChange={(e) => updateField('productNameModel', e.target.value)} className={errors.productNameModel ? 'border-red-500' : ''} data-testid="wb-product-name-input" />
                    </FormField>
                    {/* Country dropdown */}
                    <div className="space-y-1">
                      <Label className="text-sm">Country of Manufacturer</Label>
                      <div className="relative" ref={countryRef}>
                        <button type="button" onClick={() => setCountryDropdownOpen(!countryDropdownOpen)}
                          className={`w-full flex items-center justify-between px-3 py-2 border rounded-md text-sm ${errors.countryCode ? 'border-red-400' : 'border-slate-300'} bg-white hover:bg-slate-50`}
                          data-testid="wb-country-dropdown"
                        >
                          <span className={selectedCountry ? 'text-slate-900' : 'text-slate-400'}>{selectedCountry?.name || 'Select country...'}</span>
                          <ChevronDown className="h-4 w-4 text-slate-400" />
                        </button>
                        {countryDropdownOpen && (
                          <div className="absolute z-50 mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-lg max-h-52 overflow-auto">
                            <div className="sticky top-0 bg-white p-2 border-b"><div className="relative"><Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" /><Input className="pl-8" placeholder="Search country..." value={countrySearch} onChange={(e) => setCountrySearch(e.target.value)} autoFocus data-testid="wb-country-search-input" /></div></div>
                            {filteredCountries.map(c => (
                              <button key={c.code} type="button" onClick={() => { updateField('countryCode', c.code); setCountryDropdownOpen(false); setCountrySearch(''); }}
                                className="w-full text-left px-3 py-2 text-sm hover:bg-orange-50 flex items-center justify-between"
                              >
                                <span>{c.name}</span>
                                {c.code === form.countryCode && <Check className="h-4 w-4 text-orange-500" />}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                      {errors.countryCode && <p className="text-xs text-red-500">{errors.countryCode}</p>}
                    </div>
                  </div>
                </div>
                {/* WRB Type & Scope Coverage — below the main grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField label="WRB Type" required error={errors.wrbType}>
                    <FormSelect value={form.wrbType} onValueChange={(v) => updateField('wrbType', v)} placeholder="Select WRB Type" options={['Sheet', 'Fluid', 'Drainage']} error={errors.wrbType} testId="wb-wrb-type-select" />
                  </FormField>
                  <FormField label="Scope Coverage" required error={errors.scopeCoverage}>
                    <FormSelect value={form.scopeCoverage} onValueChange={(v) => updateField('scopeCoverage', v)} placeholder="Select Scope Coverage" options={['Above-grade', 'Sheathing', 'Backup wall']} error={errors.scopeCoverage} testId="wb-scope-coverage-select" />
                  </FormField>
                </div>
              </div>
            )}

            {/* ════════════ STEP 2: Material & Physical Properties ════════════ */}
            {step === 2 && (
              <div className="space-y-4" data-testid="wb-wizard-step-2">
                <h4 className="font-semibold text-sm">Material & Physical Properties</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField label="Material Type">
                    <FormSelect value={form.materialProperties.materialType} onValueChange={(v) => updateNested('materialProperties', 'materialType', v)} placeholder="Select..." options={['Spun-bond polyolefin', 'Rubberized asphalt', 'Acrylic / Polymer-based']} testId="wb-material-type-select" />
                  </FormField>
                  <FormField label="Thickness (inch)" error={errors['materialProperties.thickness']}>
                    <Input placeholder="e.g., 1.4" value={form.materialProperties.thickness} onChange={(e) => { const v = e.target.value; if (v === '' || /^\d*\.?\d*$/.test(v)) updateNested('materialProperties', 'thickness', v); }} inputMode="decimal" data-testid="wb-thickness-input" />
                  </FormField>
                  <FormField label="Reinforcement">
                    <FormSelect value={form.materialProperties.reinforcement} onValueChange={(v) => updateNested('materialProperties', 'reinforcement', v)} placeholder="Select..." options={['None', 'Scrim']} testId="wb-reinforcement-select" />
                  </FormField>
                  <FormField label="Surface Type">
                    <FormSelect value={form.materialProperties.surfaceType} onValueChange={(v) => updateNested('materialProperties', 'surfaceType', v)} placeholder="Select..." options={['None', 'Textured', 'Smooth']} testId="wb-surface-type-select" />
                  </FormField>
                </div>
              </div>
            )}

            {/* ════════════ STEP 3: Water Resistance & Drainage ════════════ */}
            {step === 3 && (
              <div className="space-y-4" data-testid="wb-wizard-step-3">
                <h4 className="font-semibold text-sm">Water Resistance & Drainage</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField label="Water Resistance Test" required error={errors['waterResistance.waterResistanceTest']}>
                    <FormSelect value={form.waterResistance.waterResistanceTest} onValueChange={(v) => updateNested('waterResistance', 'waterResistanceTest', v)} placeholder="Select..." options={['ASTM D779', 'AATCC 127']} error={errors['waterResistance.waterResistanceTest']} testId="wb-water-test-select" />
                  </FormField>
                  <FormField label="Hydrostatic Pressure Rating (cm H₂O)" error={errors['waterResistance.hydrostaticPressureRating']}>
                    <Input placeholder="e.g., 55" value={form.waterResistance.hydrostaticPressureRating} onChange={(e) => { const v = e.target.value; if (v === '' || /^\d*\.?\d*$/.test(v)) updateNested('waterResistance', 'hydrostaticPressureRating', v); }} inputMode="decimal" data-testid="wb-hydrostatic-input" />
                  </FormField>
                  <FormField label="Drainage Plane Capability" required error={errors['waterResistance.drainagePlaneCapability']}>
                    <FormSelect value={form.waterResistance.drainagePlaneCapability} onValueChange={(v) => updateNested('waterResistance', 'drainagePlaneCapability', v)} placeholder="Select..." options={['Yes', 'No']} error={errors['waterResistance.drainagePlaneCapability']} testId="wb-drainage-plane-select" />
                  </FormField>
                  <FormField label="Integrated Drainage Gap (in.)" error={errors['waterResistance.integratedDrainageGap']}>
                    <Input placeholder="e.g., 1/4 or 0.25" value={form.waterResistance.integratedDrainageGap} onChange={(e) => { const v = e.target.value; if (v === '' || /^[\d./]*$/.test(v)) updateNested('waterResistance', 'integratedDrainageGap', v); }} data-testid="wb-drainage-gap-input" />
                  </FormField>
                </div>
              </div>
            )}

            {/* ════════════ STEP 4: Vapor Permeability ════════════ */}
            {step === 4 && (
              <div className="space-y-4" data-testid="wb-wizard-step-4">
                <h4 className="font-semibold text-sm">Vapor Permeability</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField label="Vapor Permeance (perms)">
                    <Input placeholder="e.g., 58" value={form.vaporPermeability.vaporPermeance} onChange={(e) => { const v = e.target.value; if (v === '' || /^\d*\.?\d*$/.test(v)) updateNested('vaporPermeability', 'vaporPermeance', v); }} inputMode="decimal" data-testid="wb-vapor-permeance-input" />
                  </FormField>
                  <FormField label="ASTM E96 Method" required error={errors['vaporPermeability.astmE96Method']}>
                    <FormSelect value={form.vaporPermeability.astmE96Method} onValueChange={(v) => updateNested('vaporPermeability', 'astmE96Method', v)} placeholder="Select..." options={['Yes', 'No']} error={errors['vaporPermeability.astmE96Method']} testId="wb-astm-e96-select" />
                  </FormField>
                  <FormField label="Vapor Classification" required error={errors['vaporPermeability.vaporClassification']}>
                    <FormSelect value={form.vaporPermeability.vaporClassification} onValueChange={(v) => updateNested('vaporPermeability', 'vaporClassification', v)} placeholder="Select..." options={['Vapor Open', 'Vapor Semi-Impermeable', 'Vapor Closed']} error={errors['vaporPermeability.vaporClassification']} testId="wb-vapor-class-select" />
                  </FormField>
                </div>
              </div>
            )}

            {/* ════════════ STEP 5: Fire & Code Compliance ════════════ */}
            {step === 5 && (
              <div className="space-y-4" data-testid="wb-wizard-step-5">
                <h4 className="font-semibold text-sm">Fire & Code Compliance</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField label="NFPA 285 Compatibility" required error={errors['fireCodeCompliance.nfpa285Compatibility']}>
                    <FormSelect value={form.fireCodeCompliance.nfpa285Compatibility} onValueChange={(v) => updateNested('fireCodeCompliance', 'nfpa285Compatibility', v)} placeholder="Select..." options={['Yes', 'No']} error={errors['fireCodeCompliance.nfpa285Compatibility']} testId="wb-nfpa285-select" />
                  </FormField>
                  <FormField label="ASTM E84" required error={errors['fireCodeCompliance.astmE84']}>
                    <FormSelect value={form.fireCodeCompliance.astmE84} onValueChange={(v) => updateNested('fireCodeCompliance', 'astmE84', v)} placeholder="Select..." options={['FSI', 'SDI']} error={errors['fireCodeCompliance.astmE84']} testId="wb-astm-e84-select" />
                  </FormField>
                  <FormField label="ICC-ES Report No">
                    <Input placeholder="e.g., ESR-1403" value={form.fireCodeCompliance.iccEsReportNo} onChange={(e) => updateNested('fireCodeCompliance', 'iccEsReportNo', e.target.value)} data-testid="wb-icc-es-input" />
                  </FormField>
                  <FormField label="Exterior Wall Assembly Reference">
                    <Input placeholder="e.g., NFPA 285 Assembly #12" value={form.fireCodeCompliance.exteriorWallAssemblyRef} onChange={(e) => updateNested('fireCodeCompliance', 'exteriorWallAssemblyRef', e.target.value)} data-testid="wb-ext-wall-ref-input" />
                  </FormField>
                </div>
              </div>
            )}

            {/* ════════════ STEP 6: Installation Parameters ════════════ */}
            {step === 6 && (
              <div className="space-y-4" data-testid="wb-wizard-step-6">
                <h4 className="font-semibold text-sm">Installation Parameters</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField label="Application Method" required error={errors['installationParameters.applicationMethod']}>
                    <FormSelect value={form.installationParameters.applicationMethod} onValueChange={(v) => updateNested('installationParameters', 'applicationMethod', v)} placeholder="Select..." options={['Mechanically fastened', 'Self-adhered', 'Spray / Roller']} error={errors['installationParameters.applicationMethod']} testId="wb-application-method-select" />
                  </FormField>
                  <FormField label="Substrate Type" required error={errors['installationParameters.substrateType']}>
                    <FormSelect value={form.installationParameters.substrateType} onValueChange={(v) => updateNested('installationParameters', 'substrateType', v)} placeholder="Select..." options={['Gypsum sheathing', 'Concrete', 'CMU']} error={errors['installationParameters.substrateType']} testId="wb-substrate-type-select" />
                  </FormField>
                  <FormField label="Max Fastener Spacing">
                    <Input placeholder="e.g., 12 in. O.C." value={form.installationParameters.maxFastenerSpacing} onChange={(e) => updateNested('installationParameters', 'maxFastenerSpacing', e.target.value)} data-testid="wb-max-fastener-spacing-input" />
                  </FormField>
                  <FormField label="Lapping Requirements (inch)" error={errors['installationParameters.lappingRequirements']}>
                    <Input placeholder="e.g., 4" value={form.installationParameters.lappingRequirements} onChange={(e) => { const v = e.target.value; if (v === '' || /^\d*\.?\d*$/.test(v)) updateNested('installationParameters', 'lappingRequirements', v); }} inputMode="decimal" data-testid="wb-lapping-requirements-input" />
                  </FormField>
                  <div className="md:col-span-2">
                    <FormField label="Temp & Weather Limitations">
                      <Input placeholder="e.g., Do not apply below 40°F" value={form.installationParameters.tempWeatherLimitations} onChange={(e) => updateNested('installationParameters', 'tempWeatherLimitations', e.target.value)} data-testid="wb-temp-weather-input" />
                    </FormField>
                  </div>
                </div>
              </div>
            )}

            {/* ════════════ STEP 7: Interface & Continuity ════════════ */}
            {step === 7 && (
              <div className="space-y-4" data-testid="wb-wizard-step-7">
                <h4 className="font-semibold text-sm">Interface & Continuity</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField label="Interfaces with" required error={errors['interfaceContinuity.interfacesWith']}>
                    <FormSelect value={form.interfaceContinuity.interfacesWith} onValueChange={(v) => updateNested('interfaceContinuity', 'interfacesWith', v)} placeholder="Select..." options={['Thermal Insulation (CI)', 'Air Barrier', 'Flashings', 'Windows / Curtain Wall']} error={errors['interfaceContinuity.interfacesWith']} testId="wb-interfaces-with-select" />
                  </FormField>
                  <FormField label="Transition Membrane Required?" required error={errors['interfaceContinuity.transitionMembraneRequired']}>
                    <FormSelect value={form.interfaceContinuity.transitionMembraneRequired} onValueChange={(v) => updateNested('interfaceContinuity', 'transitionMembraneRequired', v)} placeholder="Select..." options={['Yes', 'No']} error={errors['interfaceContinuity.transitionMembraneRequired']} testId="wb-transition-membrane-select" />
                  </FormField>
                  <FormField label="Continuity Responsibility" required error={errors['interfaceContinuity.continuityResponsibility']}>
                    <FormSelect value={form.interfaceContinuity.continuityResponsibility} onValueChange={(v) => { updateNested('interfaceContinuity', 'continuityResponsibility', v); if (v !== 'Other') updateNested('interfaceContinuity', 'otherSubcontractor', ''); }} placeholder="Select..." options={['This subcontractor', 'Other']} error={errors['interfaceContinuity.continuityResponsibility']} testId="wb-continuity-resp-select" />
                  </FormField>
                  {form.interfaceContinuity.continuityResponsibility === 'Other' && (
                    <FormField label="Specify Party / Subcontractor" required error={errors['interfaceContinuity.otherSubcontractor']}>
                      <Input placeholder="Enter party or subcontractor name" value={form.interfaceContinuity.otherSubcontractor} onChange={(e) => updateNested('interfaceContinuity', 'otherSubcontractor', e.target.value)} data-testid="wb-other-subcontractor-input" />
                    </FormField>
                  )}
                </div>
              </div>
            )}

            {/* ════════════ STEP 8: Durability & Exposure Limits ════════════ */}
            {step === 8 && (
              <div className="space-y-4" data-testid="wb-wizard-step-8">
                <h4 className="font-semibold text-sm">Durability & Exposure Limits</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField label="UV Exposure Limit (days)" error={errors['durabilityExposure.uvExposureLimit']}>
                    <Input placeholder="e.g., 90" value={form.durabilityExposure.uvExposureLimit} onChange={(e) => { const v = e.target.value; if (v === '' || /^\d*\.?\d*$/.test(v)) updateNested('durabilityExposure', 'uvExposureLimit', v); }} inputMode="decimal" data-testid="wb-uv-exposure-input" />
                  </FormField>
                  <FormField label="Tear Resistance (lbf)">
                    <Input placeholder="e.g., 15" value={form.durabilityExposure.tearResistance} onChange={(e) => { const v = e.target.value; if (v === '' || /^\d*\.?\d*$/.test(v)) updateNested('durabilityExposure', 'tearResistance', v); }} inputMode="decimal" data-testid="wb-tear-resistance-input" />
                  </FormField>
                  <FormField label="Repair Method (If damaged)">
                    <Input placeholder="e.g., Patch with self-adhered membrane" value={form.durabilityExposure.repairMethod} onChange={(e) => updateNested('durabilityExposure', 'repairMethod', e.target.value)} data-testid="wb-repair-method-input" />
                  </FormField>
                  <FormField label="Temporary Exposure Allowed?" required error={errors['durabilityExposure.temporaryExposureAllowed']}>
                    <FormSelect value={form.durabilityExposure.temporaryExposureAllowed} onValueChange={(v) => updateNested('durabilityExposure', 'temporaryExposureAllowed', v)} placeholder="Select..." options={['Yes', 'No']} error={errors['durabilityExposure.temporaryExposureAllowed']} testId="wb-temp-exposure-select" />
                  </FormField>
                </div>
              </div>
            )}

            {/* ════════════ STEP 9: Submittals ════════════ */}
            {step === 9 && (
                <WizardSubmittalsStep
                  displayList={submittals.displayList}
                  onAddUpload={submittals.addUpload}
                  onRemoveItem={submittals.removeItem}
                  onUndoDelete={submittals.undoDelete}
                  onPreview={handlePreviewSubmittal}
                  docTypes={DOCUMENT_TYPES}
                />
            )}
            <SubmittalPreviewModal open={!!previewSubmittal} onClose={() => setPreviewSubmittal(null)} submittal={previewSubmittal} />

            {/* ════════════ STEP 10: Field Verification ════════════ */}
            {step === 10 && (() => {
              const specThickness = parseFractionValue(form.materialProperties?.thickness);
              const installedVal = parseFractionValue(form.fieldVerification.installedThickness);
              const showThicknessWarning = specThickness !== null && installedVal !== null && installedVal < specThickness;
              const isNonConformance = form.fieldVerification.nonConformance === 'Yes';
              const photos = form.fieldVerification.installationPhotos || [];
              return (
                <div className="space-y-4" data-testid="wb-wizard-step-10">
                  <h4 className="font-semibold text-sm border-b pb-2 mb-3">Field Verification <span className="text-xs text-slate-400 font-normal">(Optional)</span></h4>
                  {/* Row 1: Installed Thickness + Non-conformance */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormField label="Installed Thickness (field verified) (in)" tooltip="Thickness measured on site after installation">
                      <Input placeholder="e.g., 2, 1.5, 1/2, 1 1/2" value={form.fieldVerification.installedThickness}
                        onChange={(e) => { const v = e.target.value; if (v === '' || /^[\d./ ]*$/.test(v)) setForm(prev => ({ ...prev, fieldVerification: { ...prev.fieldVerification, installedThickness: v } })); }}
                        data-testid="wb-fv-installed-thickness" />
                      {showThicknessWarning && (
                        <div className="flex items-center gap-1.5 mt-1 text-amber-600 text-xs" data-testid="wb-fv-thickness-warning">
                          <Info className="h-3.5 w-3.5 shrink-0" /><span>Installed thickness is lower than specified ({form.materialProperties.thickness} in)</span>
                        </div>
                      )}
                    </FormField>
                    <FormField label="Non-conformance detected">
                      <div className="flex gap-2" data-testid="wb-fv-nonconformance-toggle">
                        {['Yes', 'No'].map(val => {
                          const selected = form.fieldVerification.nonConformance === val;
                          const colorClass = val === 'Yes'
                            ? (selected ? 'bg-red-500 text-white border-red-500 hover:bg-red-600' : 'hover:bg-red-50 hover:border-red-300')
                            : (selected ? 'bg-green-600 text-white border-green-600 hover:bg-green-700' : 'hover:bg-green-50 hover:border-green-300');
                          return (
                            <Badge key={val} variant="outline" className={`cursor-pointer transition-colors px-4 py-1.5 text-sm ${colorClass}`}
                              onClick={() => setForm(prev => ({ ...prev, fieldVerification: { ...prev.fieldVerification, nonConformance: prev.fieldVerification.nonConformance === val ? '' : val } }))}
                              data-testid={`wb-fv-nc-${val.toLowerCase()}`}>{val}</Badge>
                          );
                        })}
                      </div>
                    </FormField>
                  </div>
                  {/* Row 2: Inspector Comments (full width) */}
                  <FormField label={`Inspector Comments${isNonConformance ? ' *' : ''}`} error={errors['fieldVerification.inspectorComments']}>
                    <Textarea placeholder="Enter field inspection notes" maxLength={1000} rows={4}
                      value={form.fieldVerification.inspectorComments}
                      onChange={(e) => { setForm(prev => ({ ...prev, fieldVerification: { ...prev.fieldVerification, inspectorComments: e.target.value } })); if (e.target.value.trim()) setErrors(prev => ({ ...prev, 'fieldVerification.inspectorComments': null })); }}
                      className={errors['fieldVerification.inspectorComments'] ? 'border-red-500' : ''} data-testid="wb-fv-inspector-comments" />
                    <p className="text-[10px] text-slate-400 text-right mt-0.5">{(form.fieldVerification.inspectorComments || '').length}/1000</p>
                  </FormField>
                  {/* Row 3: Installation Photos (full width) */}
                  <div>
                    <div className="flex items-center justify-between mb-1.5">
                      <Label className="text-sm font-medium">Installation Photos <span className="text-slate-400 font-normal">(max 20 photos)</span></Label>
                      {photos.length < 20 && !photoUploading && (
                        <label className="inline-flex items-center gap-1.5 cursor-pointer text-xs font-medium text-slate-600 hover:bg-orange-500 hover:text-white border rounded-md px-2.5 py-1 hover:border-orange-500 transition-colors" data-testid="wb-fv-upload-photo-area">
                          <Upload className="h-3.5 w-3.5" />Upload Photo
                          <input type="file" accept="image/jpeg,image/png" multiple className="hidden" data-testid="wb-fv-photo-input" onChange={async (e) => {
                            const files = Array.from(e.target.files || []);
                            const remaining = 20 - photos.length;
                            const toUpload = files.slice(0, remaining);
                            if (files.length > remaining) toast.error(`Maximum 20 photos allowed. Only first ${remaining} will be uploaded.`);
                            setPhotoUploading(true);
                            try {
                              for (const f of toUpload) {
                                if (!['image/jpeg', 'image/png'].includes(f.type)) { toast.error(`${f.name}: Only JPG and PNG allowed`); continue; }
                                const fd = new FormData();
                                fd.append('file', f);
                                const res = await axios.post(`${API}/projects/${projectId}/field-verification/upload-photo`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
                                const p = res.data;
                                setForm(prev => ({ ...prev, fieldVerification: { ...prev.fieldVerification, installationPhotos: [...(prev.fieldVerification.installationPhotos || []), { url: p.url, publicId: p.publicId, fileName: p.fileName, uploadedAt: p.uploadedAt, takenAt: f.lastModified ? new Date(f.lastModified).toISOString() : null }] } }));
                              }
                              toast.success('Photo(s) uploaded');
                            } catch { toast.error('Photo upload failed'); }
                            finally { setPhotoUploading(false); e.target.value = ''; }
                          }} />
                        </label>
                      )}
                    </div>
                    {photoUploading && (
                      <div className="flex items-center gap-3 bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 mb-2">
                        <Loader2 className="h-5 w-5 animate-spin text-blue-600" /><p className="text-sm font-medium text-blue-800">Uploading photo...</p>
                      </div>
                    )}
                    {photos.length > 0 && (
                      <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2" data-testid="wb-fv-photo-grid">
                        {photos.map((photo, i) => (
                          <div key={i} className="relative group border rounded-lg overflow-hidden bg-slate-50 cursor-pointer" data-testid={`wb-fv-photo-${i}`} onClick={() => setViewerPhotoIndex(i)}>
                            <img src={photo.url} alt={photo.fileName} className="w-full h-24 object-cover" />
                            <button type="button" className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity z-10"
                              onClick={(e) => { e.stopPropagation(); setForm(prev => ({ ...prev, fieldVerification: { ...prev.fieldVerification, installationPhotos: prev.fieldVerification.installationPhotos.filter((_, j) => j !== i) } })); }}
                              data-testid={`wb-fv-remove-photo-${i}`}><X className="h-3 w-3" /></button>
                            <div className="absolute bottom-0 inset-x-0 bg-black/60 px-1.5 py-0.5">
                              <p className="text-[9px] text-white truncate">{photo.fileName}</p>
                              <p className="text-[8px] text-white/70">{photo.uploadedAt ? new Date(photo.uploadedAt).toLocaleDateString() : ''}{photo.takenAt ? ` | Taken: ${new Date(photo.takenAt).toLocaleDateString()}` : ''}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    {photos.length === 0 && !photoUploading && <p className="text-xs text-slate-400">No photos uploaded yet</p>}
                    {photos.length >= 20 && <p className="text-xs text-slate-400 mt-1">Maximum 20 photos reached</p>}
                  </div>
                </div>
              );
            })()}

            {/* ════════════ STEP 11: Review Summary ════════════ */}
            {isReviewStep && (
              <div className="space-y-1" data-testid="wb-wizard-step-11">
                <h4 className="font-semibold text-sm border-b pb-2 mb-3">Review Summary</h4>
                <div className="bg-slate-50 rounded-lg p-4 divide-y divide-slate-200">
                  <ReviewProductHeader
                    imagePreview={form.imagePreview}
                    qrToken={form.qrToken}
                    testIdPrefix="wb"
                    infoRows={[
                      ['Product Name / Model', form.productNameModel],
                      ['Manufacturer', selectedManufacturer?.name],
                      ['Country of Manufacturer', selectedCountry?.name],
                      ['WRB Type', form.wrbType || '—'],
                      ['Scope Coverage', form.scopeCoverage || '—'],
                    ]}
                  />
                  <div className="pt-3">
                    <ReviewSection title="Material & Physical Properties" rows={[
                      ['Material Type', form.materialProperties.materialType || '—'],
                      ['Thickness (inch)', form.materialProperties.thickness || '—'],
                      ['Reinforcement', form.materialProperties.reinforcement || '—'],
                      ['Surface Type', form.materialProperties.surfaceType || '—'],
                    ]} />
                  </div>
                  <div className="pt-3">
                    <ReviewSection title="Water Resistance & Drainage" rows={[
                      ['Water Resistance Test', form.waterResistance.waterResistanceTest || '—'],
                      ['Hydrostatic Pressure Rating (cm H₂O)', form.waterResistance.hydrostaticPressureRating || '—'],
                      ['Drainage Plane Capability', form.waterResistance.drainagePlaneCapability || '—'],
                      ['Integrated Drainage Gap (in.)', form.waterResistance.integratedDrainageGap || '—'],
                    ]} />
                  </div>
                  <div className="pt-3">
                    <ReviewSection title="Vapor Permeability" rows={[
                      ['Vapor Permeance (perms)', form.vaporPermeability.vaporPermeance || '—'],
                      ['ASTM E96 Method', form.vaporPermeability.astmE96Method || '—'],
                      ['Vapor Classification', form.vaporPermeability.vaporClassification || '—'],
                    ]} />
                  </div>
                  <div className="pt-3">
                    <ReviewSection title="Fire & Code Compliance" rows={[
                      ['NFPA 285 Compatibility', form.fireCodeCompliance.nfpa285Compatibility
                        ? <span className={`inline-flex items-center px-2 py-0.5 rounded ${form.fireCodeCompliance.nfpa285Compatibility === 'Yes' ? 'bg-green-600 text-white' : 'bg-red-600 text-white'}`}>{form.fireCodeCompliance.nfpa285Compatibility}</span>
                        : '—'],
                      ['ASTM E84', form.fireCodeCompliance.astmE84 || '—'],
                      ['ICC-ES Report No', form.fireCodeCompliance.iccEsReportNo || '—'],
                      ['Exterior Wall Assembly Reference', form.fireCodeCompliance.exteriorWallAssemblyRef || '—'],
                    ]} />
                  </div>
                  <div className="pt-3">
                    <ReviewSection title="Installation Parameters" rows={[
                      ['Application Method', form.installationParameters.applicationMethod || '—'],
                      ['Substrate Type', form.installationParameters.substrateType || '—'],
                      ['Max Fastener Spacing', form.installationParameters.maxFastenerSpacing || '—'],
                      ['Lapping Requirements (inch)', form.installationParameters.lappingRequirements || '—'],
                      ['Temp & Weather Limitations', form.installationParameters.tempWeatherLimitations || '—'],
                    ]} />
                  </div>
                  <div className="pt-3">
                    <ReviewSection title="Interface & Continuity" rows={[
                      ['Interfaces with', form.interfaceContinuity.interfacesWith || '—'],
                      ['Transition Membrane Required?', form.interfaceContinuity.transitionMembraneRequired || '—'],
                      ['Continuity Responsibility', form.interfaceContinuity.continuityResponsibility || '—'],
                      ...(form.interfaceContinuity.continuityResponsibility === 'Other' ? [['Other Subcontractor', form.interfaceContinuity.otherSubcontractor || '—']] : []),
                    ]} />
                  </div>
                  <div className="pt-3">
                    <ReviewSection title="Durability & Exposure Limits" rows={[
                      ['UV Exposure Limit (days)', form.durabilityExposure.uvExposureLimit || '—'],
                      ['Tear Resistance (lbf)', form.durabilityExposure.tearResistance || '—'],
                      ['Repair Method (If damaged)', form.durabilityExposure.repairMethod || '—'],
                      ['Temporary Exposure Allowed?', form.durabilityExposure.temporaryExposureAllowed || '—'],
                    ]} />
                  </div>
                  <WizardSubmittalsReview displayList={submittals.displayList} docTypes={DOCUMENT_TYPES} />
                  <div className="pt-3">
                    <ReviewSection title="Field Verification" rows={[
                      ['Installed Thickness', form.fieldVerification.installedThickness ? `${form.fieldVerification.installedThickness} in` : null],
                      ['Non-conformance', form.fieldVerification.nonConformance ? (() => {
                        const v = form.fieldVerification.nonConformance;
                        if (v === 'Yes') return <span className="inline-flex items-center px-2 py-0.5 rounded bg-red-500 text-white text-xs">Yes</span>;
                        if (v === 'No') return <span className="inline-flex items-center px-2 py-0.5 rounded bg-green-600 text-white text-xs">No</span>;
                        return v;
                      })() : null],
                      ['Inspector Comments', form.fieldVerification.inspectorComments || null],
                      ['Installation Photos', form.fieldVerification.installationPhotos?.length ? `${form.fieldVerification.installationPhotos.length} photo(s)` : null],
                    ]} />
                    {form.fieldVerification.installationPhotos?.length > 0 && (
                      <div className="flex gap-2 mt-2 flex-wrap">
                        {form.fieldVerification.installationPhotos.map((p, i) => (
                          <img key={i} src={p.url} alt={p.fileName} className="w-16 h-16 object-cover rounded border" data-testid={`wb-review-fv-photo-${i}`} />
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="pt-3">
                    <ReviewSection title="Status" rows={[['Current Status', draftId ? 'Will be set to Active' : 'New — will be created as Active']]} />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-3 border-t shrink-0 flex items-center justify-between">
            <div className="flex gap-2">
              <Button variant="ghost" onClick={handleCloseAttempt} data-testid="wb-cancel-btn">Cancel</Button>
              <Button variant="outline" onClick={handleSaveDraft} disabled={saving || finalizing || !isDirty} data-testid="wb-save-draft-btn">
                {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />}Save as Draft
              </Button>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={handleBack} disabled={step === 1} data-testid="wb-back-btn"><ChevronLeft className="h-4 w-4 mr-1" />Back</Button>
              {isReviewStep ? (
                <Button onClick={handleFinalSave} disabled={finalizing || saving} data-testid="wb-final-save-btn">
                  {finalizing && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}{editProduct ? 'Save' : 'Create Product'}
                </Button>
              ) : (
                <Button onClick={handleNext} disabled={saving} data-testid="wb-next-btn">Next<ChevronRight className="h-4 w-4 ml-1" /></Button>
              )}
            </div>
          </div>
        {/* Photo Viewer Modal — rendered at DialogContent level for full coverage */}
        {viewerPhotoIndex !== null && (form.fieldVerification.installationPhotos || [])[viewerPhotoIndex] && (
          <WBPhotoViewerModal
            photos={form.fieldVerification.installationPhotos}
            currentIndex={viewerPhotoIndex}
            onIndexChange={setViewerPhotoIndex}
            onClose={() => setViewerPhotoIndex(null)}
          />
        )}
        </DialogContent>
      </Dialog>

      {/* Close confirmation */}
      <ConfirmDialog open={showCloseConfirm} title="Unsaved Changes" description="You have unsaved changes. Are you sure you want to close? Your changes will be lost."
        onConfirm={resetAndClose} onClose={() => setShowCloseConfirm(false)} confirmLabel="Close" cancelLabel="Cancel" variant="destructive"
      />

      {/* Clipboard paste modal */}
      <ClipboardPasteModal open={showPasteModal} onClose={() => setShowPasteModal(false)} onPaste={handlePasteImage} uploading={pasteUploading} />
    </>
  );
};

export default AddWeatherBarrierProductWizard;
